// Adapted from https://github.com/jimmywarting/web-byline/blob/master/index.js
import { type DSVRowArray, csvParse } from 'd3-dsv';

export class ByLineTransform implements Transformer<string, string> {
  _buffer: string[];
  _lastChunkEndedWithCR: boolean;

  constructor() {
    this._buffer = [];
    this._lastChunkEndedWithCR = false;
  }

  transform(
    chunk: string,
    controller: TransformStreamDefaultController<string>
  ) {
    // see: http://www.unicode.org/reports/tr18/#Line_Boundaries
    const lines = chunk.split(/\r\n|[\n\v\f\r\x85\u2028\u2029]/g);
    const buffer = this._buffer;

    // don't split CRLF which spans chunks
    if (this._lastChunkEndedWithCR && chunk[0] === '\n') {
      lines.shift();
    }

    if (buffer.length > 0) {
      buffer[buffer.length - 1] += lines[0];
      lines.shift();
    }

    this._lastChunkEndedWithCR = chunk[chunk.length - 1] === '\r';
    buffer.push(...lines);

    // always buffer the last (possibly partial) line
    while (buffer.length > 1) {
      const line = buffer.shift();
      // skip empty lines
      if (line?.length) controller.enqueue(line);
    }
  }

  flush(controller: TransformStreamDefaultController<string>) {
    const buffer = this._buffer;

    while (buffer.length) {
      const line = buffer.shift();
      // skip empty lines
      if (line?.length) controller.enqueue(line);
    }
  }
}

function addNestedKeys(allColumns: Set<string>, obj: unknown, prefix = '') {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return;
  }
  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    allColumns.add(fullKey);
    addNestedKeys(allColumns, obj[key], fullKey);
  }
}

export function getCsvPreview(
  file: File,
  nRows = 4
): Promise<DSVRowArray | undefined> {
  const stream = file
    .stream()
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new TransformStream(new ByLineTransform()));
  const reader = stream.getReader();
  const rows: string[] = [];
  return reader
    .read()
    .then(async function processText({
      done,
      value,
    }): Promise<DSVRowArray | undefined> {
      if (value) {
        rows.push(value);
      }
      if (rows.length < nRows && !done) {
        // read the next line
        return reader.read().then(processText);
      } else {
        // parse the first 6 lines
        const parsed = csvParse(rows.join('\n'));
        reader.releaseLock();
        await stream.cancel();
        const columns = new Set(parsed.columns);
        for (const row of parsed) {
          for (const key of Object.keys(row)) {
            try {
              const value = row[key];
              if (typeof value === 'string') {
                if (['TRUE', 'FALSE'].includes(value)) {
                  row[key] = (value.toUpperCase() === 'TRUE').toString();
                }
                const parsedValue = JSON.parse(value);
                if (parsedValue != null && typeof parsedValue === 'object') {
                  addNestedKeys(columns, parsedValue, key);
                }
                row[key] = parsedValue;
              }
            } catch (e) {
              // Do nothing
            }
          }
        }
        parsed.columns = Array.from(columns);
        return parsed;
      }
    });
}

export function getJsonlPreview(
  file: File,
  nRows = 4
): Promise<DSVRowArray | undefined> {
  const stream = file
    .stream()
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new TransformStream(new ByLineTransform()));
  const reader = stream.getReader();
  const rows: Record<string, unknown>[] = [];
  return reader
    .read()
    .then(async function processLine({
      done,
      value,
    }): Promise<DSVRowArray | undefined> {
      if (value) {
        const parsed = JSON.parse(value.trim());
        rows.push(parsed);
      }

      if (rows.length < nRows && !done) {
        // Read the next line
        return reader.read().then(processLine);
      } else {
        // Return the parsed rows
        reader.releaseLock();
        await stream.cancel();

        if (rows.length === 0) {
          return undefined;
        }

        // Convert to DSVRowArray format
        const allColumns = new Set<string>();

        for (const row of rows) {
          // Limit for very complex JSON objects
          if (allColumns.size >= 5000) {
            break;
          }
          addNestedKeys(allColumns, row);
        }

        const result = rows as DSVRowArray;
        result.columns = Array.from(allColumns);
        return result;
      }
    });
}
